import Peer from "skyway-js";

class MissingNavigatorMediaDevicesError extends Error {
  constructor(...args) {
    super(...args);

    this.name = "MissingNavigatorMediaDevicesError";
    this.message = "navigator.mediaDevices does not exist.";
  }
}

class MissingNavigatorWakeLockError extends Error {
  constructor(...args) {
    super(...args);

    this.name = "MissingNavigatorWakeLockError";
    this.message = "navigator.wakeLock does not exist.";
  }
}

export class MicrophonePermissionDeniedError extends Error {
  constructor(...args) {
    super(...args);

    this.name = "MicrophonePermissionDeniedError";
    this.message =
      "ブラウザでマイクの使用が許可されていません。お手数ですが、許可したうえで再度お試しください。";
  }
}

class PeerAlreadyUsedError extends Error {
  constructor(...args) {
    super(...args);

    this.name = "PeerAlreadyUsedError";
    this.message = "Peer is already used.";
  }
}

let globalStream, peer, wakeLock;

// options:
//   - onOpen: peer のオープン時に実行されるコールバック。引数はPeerID。
//   - onStream: コネクションのストリーム開始時に実行されるコールバック。引数は MediaStream。
//   - onClose: peer の切断時に実行されるコールバック。引数なし。
//   - onError: peer およびコネクションのエラー時に実行されるコールバック。引数はエラー。

// PeerIDをつながる相談サーバーに通知しつつ電話を発信する。
export async function startCall(options = {}) {
  if (isPeerPrepared()) {
    // すでに発信中なので先にそちらを切断すべき。
    throw new PeerAlreadyUsedError();
  }

  await prepare(options);

  // peer はマイクの使用が許可されていないとか接続直後に切断されるなどにより存在しないことがまれにある。
  if (!peer) {
    return;
  }

  if ("onOpen" in options) {
    peer.once("open", options.onOpen);
  }

  peer.once("open", () => {
    // 電話を発信する。
    peer.call(peer.id, globalStream);
  });

  peer.on("call", (connection) => {
    connection.answer(globalStream);
    onCall(connection, options);
  });
}

// 着信した電話に出る。
export async function startTalk(peerId, options = {}) {
  if (isPeerPrepared()) {
    // すでに発信中なので先にそちらを切断すべき。
    throw new PeerAlreadyUsedError();
  }

  await prepare(options);

  // peer はマイクの使用が許可されていないとか接続直後に切断されるなどにより存在しないことがまれにある。
  if (peer) {
    peer.once("open", () => {
      const connection = peer.call(peerId, globalStream);
      onCall(connection, options);
    });
  }
}

// 切断する（発信を中止する / 通話を切る）。
export function finish() {
  teardown();
}

// 通話が可能かチェックし、可能な場合は peer と stream を初期化する。
async function prepare(options = {}) {
  // すでに初期化済みなら何もしない。
  if (isPeerPrepared()) {
    return;
  }

  const { isAvailable, stream, error } = await checkAvailability();

  if (isAvailable) {
    preparePeer(options);
    globalStream = stream;
    keepScreenOn();
  } else {
    console.error("An error happened:", error);

    if ("onError" in options) {
      options.onError(error);
    }
  }
}

// peer の初期化が済んでいるかどうか判定し true/false を返す。
function isPeerPrepared() {
  // 未初期化なら peer は undefined、一度初期化されたが teardown() で破棄されていたら null。
  return peer instanceof Peer;
}

// peer を初期化する。
function preparePeer(options = {}) {
  try {
    peer = new Peer({ key: process.env.SKYWAY_API_KEY });
  } catch (error) {
    console.error("An error happened:", error);

    if ("onError" in options) {
      options.onError(error);
    }

    teardown();
  }

  if (peer) {
    // close イベントが呼ばれるのはあくまで自分が切断したとき。相手が切断しても呼ばれないので注意。
    if ("onClose" in options) {
      peer.on("close", options.onClose);
    }

    if ("onError" in options) {
      peer.on("error", (error) => {
        console.error("An error happened:", error);
        options.onError(error);
      });
    }

    peer.on("error", teardown);
  }
}

// 通話が可能かどうかチェックする。
//   - navigator.mediaDevices が存在するかどうか
//   - 音声利用がユーザーから許可されているかどうか
async function checkAvailability() {
  if (typeof navigator !== "undefined" && "mediaDevices" in navigator) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });

      return { isAvailable: true, stream };
    } catch (error) {
      let returnError = error;

      if (error.name === "NotAllowedError") {
        returnError = new MicrophonePermissionDeniedError();
      }

      return { isAvailable: false, error: returnError };
    }
  } else {
    return {
      isAvailable: false,
      error: new MissingNavigatorMediaDevicesError(),
    };
  }
}

// 端末の画面オフ/スリープを無効化する。スリープすると通話が強制終了してしまうため。
// 検証した限りではiOS Safariなら通話の音声が途切れない限りは端末がスリープせず通話が継続した。したがって、iOS Safariは
// このスリープ無効化処理の対象外。
// 対象はAndroid Chrome。Android Chromeでは端末がスリープして通話が終了してしまったため navigator.wakeLock で画面オフ無
// 効化/スリープ無効化処理を行っている。
// 2022/06末現在、 navigator.wakeLock はv84以降のChrome（系ブラウザ）でしかサポートされていない。したがって古いAndroid端
// 末（古いChrome）では動かない可能性がある。
// cf. https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
async function keepScreenOn() {
  if (typeof navigator !== "undefined" && "wakeLock" in navigator) {
    try {
      wakeLock = await navigator.wakeLock.request("screen");
    } catch (error) {
      // ここに到達するのは、端末のバッテリー節約モードなどによりスリープや画面オフの無効化が不可能なケース。ユーザーへ
      // フィードバックしたところでどうしようもないケースが多いためコンソール上にエラーを出力するだけとしている。
      console.error(error);
    }
  } else {
    // navigator.wakeLock はサポートされていないブラウザが多い。そのためユーザーへのフィードバックを行うと煩わしすぎると
    // 判断しコンソール上にエラーを出力するだけとしている。
    console.error(new MissingNavigatorWakeLockError());
  }
}

// peer の call イベント発生時あるいは call() メソッド実行後に実行される処理。
function onCall(connection, options = {}) {
  // peer と重複してしまうため connection には on("close", options.onClose) を行わないこと。
  connection.on("stream", startStream);
  connection.on("error", teardown);

  if ("onStream" in options) {
    connection.on("stream", options.onStream);
  }

  if ("onError" in options) {
    connection.on("error", (error) => {
      console.error("An error happened:", error);
      options.onError(error);
    });
  }
}

// コネクションのストリーム開始時に実行されるベースの処理。
function startStream(stream) {
  const audio = findAudioElement();
  audio.srcObject = stream;
  audio.play();
}

// peer の切断時に実行されるベースの処理。
async function teardown() {
  if (globalStream) {
    // マイクの使用を停止する。
    globalStream.getTracks().forEach((track) => track.stop());

    // ストリームを使い回すとマイクが有効化されないことがあるので毎回リセットする。
    globalStream = null;
  }

  const audio = findAudioElement();
  audio.srcObject = null;

  // コネクションの後始末などが厄介なので切断のたびに peer 自身を破棄する。切断するだけだと、 peer がコネクションを保持
  // し続けてしまう。単純にメモリの無駄だし、 peer が発信中/通話中なのかの判定も面倒。毎回破棄することでクリーンな状態を
  // 保っておく。
  if (peer) {
    peer.destroy();
    peer = null;
  }

  if (wakeLock) {
    // 後始末: 端末の画面オフ無効化/スリープ無効化をキャンセルする。通話していないときはスリープしても問題ない。
    //
    // WakeLockSentinel のインスタンスメソッド release() を呼び出しているドキュメントもあれば WakeLockSentinel.release()
    // の形でスタティックメソッドを呼び出しているドキュメントもある。ドキュメントもが間違っているだけなのか両方の実装が
    // ありうるのか判断できない。よって呼び出せるかどうかをチェックして呼び出せる場合は呼び出すようにしている。
    if (typeof wakeLock.release === "function") {
      // https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
      await wakeLock.release();
    } else if (
      typeof WakeLockSentinel !== "undefined" &&
      typeof WakeLockSentinel.release === "function"
    ) {
      // https://developer.mozilla.org/en-US/docs/Web/API/WakeLockSentinel/release
      await WakeLockSentinel.release();
    }

    wakeLock = null;
  }
}

// 音声通話で使用する audio 要素を取得する。存在しなければ作成する。
function findAudioElement() {
  const id = "js-calling-audio";
  const element = document.querySelector(`#${id}`);

  if (element) {
    return element;
  } else {
    const $newElement = $("<audio>").prop({ id: id, autoplay: true });
    $("body").append($newElement);
    return $newElement.get(0);
  }
}
